import * as httpx from "httpx"
import * as uuid from "uuid"
import * as events from "./events"
import { CancellablePromise } from "real-cancellable-promise"
import {
	VideoSearchRequest,
	VideoSearchResponse,
	VideoUploadResponse,
	VideoPatchRequest,
	VideoPatchResponse,
	VideoVersionHistoryResponse,
} from "brandguard/brandguard.video_pb"

function upload(file: File, bid: string, ancestorId: string | undefined, ...options: httpx.option[]): CancellablePromise<VideoUploadResponse> {
	const data = new FormData()
	data.append("file", file)
	if (ancestorId) data.append("ancestor_id", ancestorId)
	return httpx
		.upload<VideoUploadResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}`, data, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
		}))
}

export function create(file: File, bid: string, ...options: httpx.option[]): CancellablePromise<VideoUploadResponse> {
	return upload(file, bid, undefined, ...options)
}

export function revise(file: File, bid: string, ancestorId: string, ...options: httpx.option[]): CancellablePromise<VideoUploadResponse> {
	return upload(file, bid, ancestorId, ...options)
}

export namespace searches {
	export function zero(sr: Partial<VideoSearchRequest> = {}): VideoSearchRequest {
		return {
			offset: 0n,
			limit: 20n,
			uploaded_by: [],
			deprecated_uploaded_by: "",
			brand_id: uuid.NIL,
			queues: [],
			approved: [],
			observation: [],
			confidence: { min: 0, max: 1 },
			...sr,
		}
	}
}

export function search(
	bid: string,
	req: VideoSearchRequest,
	...options: httpx.option[]
): CancellablePromise<VideoSearchResponse> {
	return httpx.get<VideoSearchResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}`, req, ...options).then((resp) => ({
		...resp,
		items: (resp.items || []).map((item) => ({ media: item.media, event: events.zero(item.event) })),
		next: searches.zero(resp.next),
	}))
}

export function find(id: string, bid: string, ...options: httpx.option[]): CancellablePromise<VideoUploadResponse> {
	return httpx
		.get<VideoUploadResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}/${id}`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
		}))
}

export function patch(
	bid: string,
	id: string,
	req: VideoPatchRequest,
	...options: httpx.option[]
): CancellablePromise<VideoPatchResponse> {
	return httpx
		.patch<VideoPatchResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}/${id}`, req, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
		}))
}

export function requeue(id: string, bid: string, ...options: httpx.option[]): Promise<VideoUploadResponse> {
	return httpx
		.post<VideoUploadResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}/${id}/requeue`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
		}))
}

export function review(id: string, bid: string, ...options: httpx.option[]): Promise<VideoPatchResponse> {
	return httpx
		.post<VideoPatchResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}/${id}/review`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
		}))
}

export function publish(id: string, bid: string, ...options: httpx.option[]): Promise<VideoPatchResponse> {
	return httpx
		.post<VideoPatchResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}/${id}/publish`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
		}))
}

export function destroy(bid: string, id: string, ...options: httpx.option[]): Promise<VideoPatchResponse> {
	return httpx
		.destroy<VideoPatchResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}/${id}`, {}, ...options)
		.then((resp) => ({
			media: resp.media,
			event: events.zero(resp.event),
		}))
}

export function versions(id: string, bid: string, ...options: httpx.option[]): CancellablePromise<VideoVersionHistoryResponse> {
	return httpx
		.get<VideoVersionHistoryResponse>(`${httpx.urlstorage.host()}/bg/video/${bid}/${id}/versions`, {}, ...options)
		.then((resp) => ({
			versions: (resp.versions || []).map((item) => ({ media: item.media, event: events.zero(item.event) })),
		}))
}
